import api from './api';

const reservationService = {
  // Get all user reservations from different services
  getAllUserReservations: async () => {
    try {
      // Obtenir l'utilisateur actuel pour récupérer son ID
      const currentUser = api.getCurrentUser();
      if (!currentUser || !currentUser._id) {
        throw new Error('Utilisateur non authentifié');
      }

      const userId = currentUser._id;

      const [circuits, hotels, transports, visites, voitures] = await Promise.allSettled([
        api.get('/reservations/circuits/me'),
        api.get(`/reservations/hotels/user/${userId}`),
        api.get(`/reservations/transports/user/${userId}`),
        api.get(`/reservations/visites/user/${userId}`),
        api.get(`/reservations/voitures/user/${userId}`)
      ]);

      const allReservations = [];

      // Process circuit reservations
      if (circuits.status === 'fulfilled' && circuits.value?.data?.reservations) {
        circuits.value.data.reservations.forEach(reservation => {
          allReservations.push({
            ...reservation,
            type: 'circuit',
            serviceName: reservation.circuit?.titre || 'Circuit',
            status: reservation.statut,
            date: reservation.date_debut || reservation.createdAt,
            price: reservation.prix_total
          });
        });
      }

      // Process hotel reservations
      if (hotels.status === 'fulfilled' && hotels.value?.data?.data) {
        hotels.value.data.data.forEach(reservation => {
          allReservations.push({
            ...reservation,
            type: 'hotel',
            serviceName: reservation.hotel?.nom || 'Hôtel',
            status: reservation.statut,
            date: reservation.date_debut || reservation.createdAt,
            price: reservation.prix_total
          });
        });
      }

      // Process transport reservations
      if (transports.status === 'fulfilled' && transports.value?.data) {
        const transportData = Array.isArray(transports.value.data) ? transports.value.data : [];
        transportData.forEach(reservation => {
          allReservations.push({
            ...reservation,
            type: 'transport',
            serviceName: `Transport ${reservation.pointDepart} → ${reservation.pointArrivee}`,
            status: reservation.statut,
            date: reservation.date || reservation.createdAt,
            price: reservation.prix_total
          });
        });
      }

      // Process guided tour reservations
      if (visites.status === 'fulfilled' && visites.value?.data?.data) {
        visites.value.data.data.forEach(reservation => {
          allReservations.push({
            ...reservation,
            type: 'visite',
            serviceName: reservation.visite?.description || 'Visite guidée',
            status: reservation.statut,
            date: reservation.date || reservation.createdAt,
            price: reservation.prix_total
          });
        });
      }

      // Process car rental reservations
      if (voitures.status === 'fulfilled' && voitures.value?.data?.data) {
        voitures.value.data.data.forEach(reservation => {
          allReservations.push({
            ...reservation,
            type: 'voiture',
            serviceName: `${reservation.voiture?.marque} ${reservation.voiture?.modele}` || 'Location de voiture',
            status: reservation.statut,
            date: reservation.date_debut || reservation.createdAt,
            price: reservation.prix_total
          });
        });
      }

      // Sort by date (most recent first)
      allReservations.sort((a, b) => new Date(b.date) - new Date(a.date));

      return {
        success: true,
        data: allReservations,
        count: allReservations.length
      };
    } catch (error) {
      console.error('Error fetching user reservations:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.message
      };
    }
  },

  // Get status color for display
  getStatusColor: (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmé':
      case 'confirmed':
        return 'text-green-600 bg-green-100';
      case 'en attente':
      case 'en attente de paiement':
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'annulé':
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      case 'terminé':
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  },

  // Get status text in French
  getStatusText: (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmé':
      case 'confirmed':
        return 'Confirmé';
      case 'en attente':
      case 'en attente de paiement':
      case 'pending':
        return 'En attente';
      case 'annulé':
      case 'cancelled':
        return 'Annulé';
      case 'terminé':
      case 'completed':
        return 'Terminé';
      default:
        return status || 'Inconnu';
    }
  },

  // Get service type icon
  getServiceIcon: (type) => {
    switch (type) {
      case 'circuit':
        return '🗺️';
      case 'hotel':
        return '🏨';
      case 'transport':
        return '🚗';
      case 'visite':
        return '🎯';
      case 'voiture':
        return '🚙';
      default:
        return '📋';
    }
  },

  // Get reservation details by ID and type
  getReservationById: async (reservationId, type) => {
    try {
      let endpoint = '';
      
      switch (type) {
        case 'hotel':
          endpoint = `/reservations/hotels/${reservationId}`;
          break;
        case 'circuit':
          endpoint = `/reservations/circuits/${reservationId}`;
          break;
        case 'transport':
          endpoint = `/reservations/transports/${reservationId}`;
          break;
        case 'visite':
          endpoint = `/reservations/visites/${reservationId}`;
          break;
        case 'voiture':
          endpoint = `/reservations/voitures/${reservationId}`;
          break;
        default:
          throw new Error('Type de réservation non reconnu');
      }
      
      const response = await api.get(endpoint);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error(`Error fetching ${type} reservation details:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }
};

export default reservationService;